import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { Box } from '../../atoms/Box'
import {
  InheritanceFlowChartProps,
  DEFAULT_INHERITANCE_FLOW_CHART_LABELS,
} from './InheritanceFlowChart.types'

const ChartContainer = styled.div`
  width: 100%
  height: 100%
  position: relative
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%
  height: ${({ $height }) => $height}px
`

const ErrorContainer = styled(Flex)`
  height: 350px
  color: ${({ theme }) => theme.colors.text.secondary}
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

const ChartsGrid = styled(Box)`
  display: grid
  grid-template-columns: 1fr
  gap: 20px
  width: 100%

  @media print {
    gap: 40px
  }
`

const FullWidthChart = styled(Box)`
  width: 100%;
`

export interface InheritanceFlowChartRef {
  exportChart: () => string | null
}

export const InheritanceFlowChart = forwardRef<
  InheritanceFlowChartRef,
  InheritanceFlowChartProps
>(
  (
    {
      flowData,
      shareData,
      timelineData = [],
      loading = false,
      error = null,
      height = 500,
      labels = {},
    },
    ref
  ) => {
    const mergedLabels = { ...DEFAULT_INHERITANCE_FLOW_CHART_LABELS, ...labels }
    const sankeyChartRef = useRef<HTMLDivElement>(null)
    const shareChartRef = useRef<HTMLDivElement>(null)
    const timelineChartRef = useRef<HTMLDivElement>(null)
    const sankeyChartInstance = useRef<echarts.ECharts | null>(null)
    const shareChartInstance = useRef<echarts.ECharts | null>(null)
    const timelineChartInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (sankeyChartInstance.current) {
          return sankeyChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
    }))

    useEffect(() => {
      if (!loading && !error && flowData && sankeyChartRef.current) {
        initializeSankeyChart()
      }

      return () => {
        sankeyChartInstance.current?.dispose()
      }
    }, [flowData, loading, error])

    useEffect(() => {
      if (!loading && !error && shareData && shareChartRef.current) {
        initializeShareChart()
      }

      return () => {
        shareChartInstance.current?.dispose()
      }
    }, [shareData, loading, error])

    useEffect(() => {
      if (
        !loading &&
        !error &&
        timelineData.length > 0 &&
        timelineChartRef.current
      ) {
        initializeTimelineChart()
      }

      return () => {
        timelineChartInstance.current?.dispose()
      }
    }, [timelineData, loading, error])

    useEffect(() => {
      const handleResize = () => {
        sankeyChartInstance.current?.resize()
        shareChartInstance.current?.resize()
        timelineChartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    const initializeSankeyChart = () => {
      if (!sankeyChartRef.current || !flowData) return

      if (sankeyChartInstance.current) {
        sankeyChartInstance.current.dispose()
      }

      sankeyChartInstance.current = safeChartInit(sankeyChartRef.current)

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.flowAnalysis,
          subtext: mergedLabels.flowAnalysisSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          formatter: (params: any) => {
            if (params.dataType === 'edge') {
              return `${params.data.source} → ${params.data.target}<br/>${mergedLabels.transactions}: ${params.data.value}`
            }
            return `${params.name}<br/>Total: ${params.value} ${mergedLabels.transactions}`
          },
        },
        series: [
          {
            type: 'sankey',
            /* layout: 'none', */
            emphasis: {
              focus: 'adjacency',
            },
            data: flowData.nodes,
            links: flowData.links,
            lineStyle: {
              color: 'gradient',
              curveness: 0.5,
            },
            label: {
              fontSize: 11,
              fontWeight: 500,
            },
            left: '5%',
            right: '5%',
            top: '15%',
            bottom: '5%',
          },
        ],
      }

      sankeyChartInstance.current.setOption(option)
    }

    const initializeShareChart = () => {
      if (!shareChartRef.current || !shareData) return

      if (shareChartInstance.current) {
        shareChartInstance.current.dispose()
      }

      shareChartInstance.current = safeChartInit(shareChartRef.current)

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.shareDistribution,
          subtext: mergedLabels.shareDistributionSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            return `${params.name}<br/>Count: ${params.value} (${params.percent}%)`
          },
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          top: '15%',
          data: shareData.shareRanges.map((s) => s.range),
        },
        series: [
          {
            name: 'Share Distribution',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['60%', '55%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 8,
              borderColor: '#fff',
              borderWidth: 2,
            },
            label: {
              show: true,
              formatter: '{b}: {c}',
              fontSize: 11,
            },
            emphasis: {
              label: {
                show: true,
                fontSize: 14,
                fontWeight: 'bold',
              },
            },
            data: shareData.shareRanges.map((s) => ({
              name: s.range,
              value: s.count,
            })),
          },
        ],
      }

      shareChartInstance.current.setOption(option)
    }

    const initializeTimelineChart = () => {
      if (
        !timelineChartRef.current ||
        !timelineData ||
        timelineData.length === 0
      )
        return

      if (timelineChartInstance.current) {
        timelineChartInstance.current.dispose()
      }

      timelineChartInstance.current = safeChartInit(timelineChartRef.current)

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.inheritanceOverTime,
          subtext: mergedLabels.inheritanceOverTimeSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            let result = `<div style="padding: 8px;"><div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`
            params.forEach((param: any) => {
              result += `<div>${param.marker} ${param.seriesName}: ${param.value}</div>`
            })
            result += `</div>`
            return result
          },
        },
        legend: {
          data: [
            mergedLabels.heritage!,
            mergedLabels.donation!,
            mergedLabels.partage!,
          ],
          top: '12%',
          left: 'center',
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '25%',
          bottom: '15%',
        },
        xAxis: {
          type: 'category',
          data: timelineData.map((d) => d.period),
          axisLabel: {
            rotate: 45,
            fontSize: 10,
          },
        },
        yAxis: {
          type: 'value',
          name: mergedLabels.transactionCount,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
        },
        series: [
          {
            name: mergedLabels.heritage,
            type: 'bar',
            stack: 'total',
            data: timelineData.map((d) => d.heritage),
            itemStyle: {
              color: '#5470c6',
            },
          },
          {
            name: mergedLabels.donation,
            type: 'bar',
            stack: 'total',
            data: timelineData.map((d) => d.donation),
            itemStyle: {
              color: '#91cc75',
            },
          },
          {
            name: mergedLabels.partage,
            type: 'bar',
            stack: 'total',
            data: timelineData.map((d) => d.partage),
            itemStyle: {
              color: '#fac858',
            },
          },
        ],
      }

      timelineChartInstance.current.setOption(option)
    }

    if (loading) {
      return (
        <LoadingContainer justify="center" align="center">
          <Loading size="large" />
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer justify="center" align="center">
          {error}
        </ErrorContainer>
      )
    }

    if (!flowData && !shareData && timelineData.length === 0) {
      return (
        <ErrorContainer justify="center" align="center">
          {mergedLabels.noData}
        </ErrorContainer>
      )
    }

    return (
      <ChartContainer>
        <ChartsGrid>
          {flowData && (
            <FullWidthChart>
              <ChartWrapper $height={height} ref={sankeyChartRef} />
            </FullWidthChart>
          )}
          {shareData && (
            <FullWidthChart>
              <ChartWrapper $height={400} ref={shareChartRef} />
            </FullWidthChart>
          )}
          {timelineData.length > 0 && (
            <FullWidthChart>
              <ChartWrapper $height={400} ref={timelineChartRef} />
            </FullWidthChart>
          )}
        </ChartsGrid>
      </ChartContainer>
    )
  }
)

InheritanceFlowChart.displayName = 'InheritanceFlowChart'
